/**
 * Unified styling system for all transmission components.
 *
 * This is the single source of truth for:
 * - Input and form controls
 * - Labels and text
 * - Error states
 *
 * Used by:
 * - transmission-methods/ forms (Fax, Email, Mail)
 * - transmission/components/
 */

import { cn } from '@/lib/utils';

// ============================================================================
// Input Styles
// ============================================================================

/**
 * Premium soft input styling - primary style for all text inputs.
 * Compact design with subtle focus states.
 */
export const inputStyles = cn(
  'h-9 rounded-md border border-zinc-200/60 bg-white/80 px-3',
  'text-[13px] text-zinc-700 placeholder:text-zinc-400',
  'focus:border-indigo-300/70 focus:bg-white focus:ring-1 focus:ring-indigo-100/50',
  'transition-all duration-150',
);

/**
 * Premium soft textarea styling - matches input style.
 */
export const textareaStyles = cn(
  'rounded-md border border-zinc-200/60 bg-white/80 px-3 py-2',
  'text-[13px] text-zinc-700 placeholder:text-zinc-400 leading-relaxed',
  'focus:border-indigo-300/70 focus:bg-white focus:ring-1 focus:ring-indigo-100/50',
  'transition-all duration-150 resize-none',
);

// ============================================================================
// Label Styles
// ============================================================================

/**
 * Standard label for form fields. Compact and refined.
 */
export const labelStyles =
  'text-[11px] font-medium text-zinc-500 tracking-wide';

// ============================================================================
// Error Styles
// ============================================================================

/**
 * Error message text for FormField component.
 */
export const errorStyles = 'text-[11px] text-red-500 font-medium';

/**
 * Helper text for FormField component.
 */
export const helperTextStyles = 'text-[11px] text-zinc-400';

/**
 * Input with error state - ring-based for soft inputs.
 */
export const inputErrorStyles =
  'border-red-300/70 bg-red-50/30 focus:ring-red-100/50';

/**
 * Input with warning state - for character limit approaching.
 */
export const inputWarningStyles =
  'border-amber-300/70 bg-amber-50/20 focus:ring-amber-100/50';
